import { useState } from 'react';
import { ExternalLink, Copy, Check } from 'lucide-react';

/**
 * BookingWidget page — embeds the public booking widget inside the app for testing.
 * Also shows the embed code for WordPress.
 */
export default function BookingWidget() {
  const [copied, setCopied] = useState(false);

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

  const widgetUrl = `${window.location.origin}/booking-widget.html?supabaseUrl=${encodeURIComponent(supabaseUrl)}&supabaseKey=${encodeURIComponent(supabaseKey)}`;

  const embedCode = `<!-- Errigal Table Booking Widget -->
<div id="errigal-booking"></div>
<script
  src="${window.location.origin}/embed-booking.js"
  data-supabase-url="${supabaseUrl}"
  data-supabase-key="${supabaseKey}">
</script>`;

  const iframeCode = `<iframe
  src="${widgetUrl}"
  style="width:100%;border:none;min-height:700px;max-width:520px;margin:0 auto;display:block;"
  title="Book a Table - Errigal Country House Hotel">
</iframe>`;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Booking Widget</h1>
        <p className="text-gray-500 mt-1 text-sm">Test the public table booking widget and get embed code for WordPress</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Live widget preview */}
        <div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-gray-900 text-base">Live Preview</h2>
              <a href={widgetUrl} target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                Open in new tab <ExternalLink size={12} />
              </a>
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-100" style={{ minHeight: '700px' }}>
              <iframe
                src={widgetUrl}
                title="Booking Widget Preview"
                style={{ width: '100%', border: 'none', minHeight: '700px' }}
              />
            </div>
          </div>
        </div>

        {/* Right: Embed codes */}
        <div className="space-y-5">
          {/* Status */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-bold text-gray-900 text-base mb-3">Connection Status</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Supabase URL</span>
                <span className={`font-medium ${supabaseUrl ? 'text-green-600' : 'text-red-500'}`}>
                  {supabaseUrl ? 'Connected' : 'Not configured'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Anon Key</span>
                <span className={`font-medium ${supabaseKey ? 'text-green-600' : 'text-red-500'}`}>
                  {supabaseKey ? 'Set' : 'Not configured'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Deposit</span>
                <span className="font-medium text-gray-900">€25 for 5+ covers</span>
              </div>
            </div>
          </div>

          {/* Embed Script Code */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-gray-900 text-base">WordPress Embed Code</h2>
              <button onClick={() => handleCopy(embedCode)}
                className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 transition">
                {copied ? <><Check size={12} className="text-green-600" /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">Add a Custom HTML block in WordPress and paste this code:</p>
            <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all">{embedCode}</pre>
          </div>

          {/* iframe Code */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-gray-900 text-base">Alternative: iframe Embed</h2>
              <button onClick={() => handleCopy(iframeCode)}
                className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 transition">
                {copied ? <><Check size={12} className="text-green-600" /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">If the embed script doesn't work, use an iframe instead:</p>
            <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all">{iframeCode}</pre>
          </div>

          {/* Instructions */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h3 className="font-bold text-amber-900 text-sm mb-2">Setup Checklist</h3>
            <ul className="text-xs text-amber-800 space-y-1.5">
              <li>1. Run the SQL migration in Supabase SQL Editor</li>
              <li>2. Push code to GitHub (auto-deploys to Vercel)</li>
              <li>3. Test the widget here in the app</li>
              <li>4. Copy the embed code to your WordPress page</li>
              <li>5. Test a booking from the WordPress site</li>
              <li>6. Check it appears in the Tables page</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
